import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import StepperCard from '@/components/common/StepperCard.tsx';
import { NavigationBar } from '@/components/NavigationBar';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { useSessionSave } from '@/hooks/use-session-save';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import type { RoutePath } from '@/lib/wizard/wizard';
import { getState } from '@/lib/zipcode.ts';
import { getCarrierTypesNeedingInput, setCarrierTypes } from '@/types/insurance-builder-legacy.ts';
import {
  type InsuranceBuilder,
  setQueuedInsurance,
  setUnprovidedInsurance,
} from '@/types/insurance-builder.type.ts';
import {
  CARRIER_TYPE,
  type CarrierType,
  type QueuedInsurance,
  type UnprovidedInsurance,
  parseCarrierType,
} from '@/types/insurance.type.ts';
import { InsuranceInputForm } from '@/views/insurance/flows/legacy/components/InsuranceInputForm.tsx';

export default function InsuranceInput() {
  const navigate = useNavigate();
  const { saveSession } = useSessionSave();

  const { patientRecipient, patientFirstName, insuranceZipCode, insuranceBuilder } =
    useSessionStore();
  const state = getState(insuranceZipCode);

  const { carrierType } = useSearch({ from: '/appLayout/wizardLayout/insurance/input' });

  const fixedCarrierType = parseCarrierType(carrierType ?? '');

  const navigateToNext = useCallback(
    (builder: InsuranceBuilder) => {
      // Auto-save session (non-blocking)
      saveSession();

      const missingCarrierTypes = getCarrierTypesNeedingInput(builder);
      if (missingCarrierTypes.length > 0)
        return navigate({ to: `/insurance/input?carrierType=${missingCarrierTypes[0]}` });

      if (builder.queuedInsurances.length > 0)
        return navigate({ to: '/insurance/verify-pending' as RoutePath });

      if (builder.unprovidedInsurances.length > 0) return navigate({ to: '/insurance/follow-up' });

      return navigate({ to: '/insurance/finalize' });
    },
    [navigate, saveSession]
  );

  const updateUnverified = useCallback(
    (queued: QueuedInsurance) => {
      const updatedCarriers = new Set([
        ...insuranceBuilder.carrierTypes,
        queued.carrier.carrierType,
      ]);
      const updatedBuilder = setQueuedInsurance(
        setCarrierTypes(insuranceBuilder, updatedCarriers),
        queued
      );
      useSessionStore.getState().updateState({ insuranceBuilder: updatedBuilder });

      navigateToNext(updatedBuilder);
    },
    [insuranceBuilder, navigateToNext]
  );

  const toFollowUp = useCallback(
    (insurance: UnprovidedInsurance) => {
      const updatedCarriers = new Set([...insuranceBuilder.carrierTypes, insurance.carrierType]);
      const updatedBuilder = setUnprovidedInsurance(
        setCarrierTypes(insuranceBuilder, updatedCarriers),
        insurance
      );
      useSessionStore.getState().updateState({ insuranceBuilder: updatedBuilder });

      navigateToNext(updatedBuilder);
    },
    [insuranceBuilder, navigateToNext]
  );

  const toInsuranceStart = () => navigate({ to: '/insurance/pre-discovery' });
  const initialState = useMemo(() => (state ? { state } : {}), [state]);

  return (
    <section>
      <NavigationBar onBack={toInsuranceStart} backLabel="Insurance info" stepIndicator="3 of 4" />
      <StepperCard currentStep={3} totalSteps={4}>
        <CardHeader>
          <CardTitle>{headingText(fixedCarrierType, patientRecipient, patientFirstName)}</CardTitle>
        </CardHeader>

        <CardContent>
          <InsuranceInputForm
            initialData={initialState}
            fixedCarrierType={fixedCarrierType}
            onSubmit={updateUnverified}
            onCannotProvide={toFollowUp}
          />
        </CardContent>
      </StepperCard>
    </section>
  );
}

function headingText(
  carrierType: CarrierType | undefined,
  patientRecipient: string,
  patientFirstName: string
): string {
  if (carrierType === CARRIER_TYPE.MEDICARE) {
    return 'Help us find your Medicare plan';
  }
  if (carrierType === CARRIER_TYPE.MEDICAID) {
    return "Great! Now let's get your Medicaid";
  }
  if (patientRecipient === 'me') {
    return 'Help us find your plan';
  }
  return `Help us find ${patientFirstName}'s plan`;
}
